import { useModal } from '@billy/design-system'
import { createContext, ReactElement, useContext, useEffect, useRef, useState } from 'react'

import { useBankIntegration } from '@features/openBanking/contexts/bankIntegrationContext'
import { isOpenBankingAccountDeleted } from '@features/openBanking/utils/isOpenBankingAccountDeleted'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { ModalId } from '../../../../enums/modalId'
import { useQueryParams } from '../../../../hooks/useQueryParams'
import { useToggleBookkeepingHero } from '../../../../hooks/useToggleBookkeepingHero'
import { DEFAULT_QUERY_PARAMS } from '../constants'
import { useFrodaOnboarding } from '../hooks/useFrodaOnboarding'
import { useGenerateFrodaRedirectUrl } from '../hooks/useGenerateFrodaRedirectUrl'
import { useGetOfferClick } from '../hooks/useGetOfferClick'
import { useFrodaHomeAccessUrl } from '../hooks/useRequestFrodaHomeAccessUrl'
import { FrodaLendingEligibilityRequirements } from '../types/frodaLendingEligibilityRequirements'
import { FrodaRedirectionQueryParams } from '../types/frodaRedirectionQueryParams'

interface FrodaFinanceLendingContextProps {
  eligibilityRequirements: FrodaLendingEligibilityRequirements
  homeAccessUrl: string | null | undefined
  isAccessUrlLoading: boolean
  isProcessing: boolean
  onboardUser: () => void
  onGetOfferClick: () => void
  parentUrl: string
  setIsProcessing: (value: boolean) => void
  setTermsAccepted: (value: boolean) => void
  termsAccepted: boolean
}

const FrodaFinanceLendingContext = createContext<FrodaFinanceLendingContextProps | undefined>(undefined)

export const FrodaFinanceLendingProviderContext = ({ children }: { children: ReactElement }) => {
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [queryParams] = useQueryParams<FrodaRedirectionQueryParams>({
    defaultQuery: DEFAULT_QUERY_PARAMS,
    withDefaultQueryInUrl: false,
  })

  const { user } = useCurrentUser()
  const { organization } = useUserOrganization()
  const { isBookkeepingHeroHidden, toggleBookkeepingHero } = useToggleBookkeepingHero()
  const initialIsBookkeepingHeroHiddenRef = useRef(isBookkeepingHeroHidden)

  const { openBankingAccounts } = useBankIntegration()
  const hasConnectedBank = !!openBankingAccounts?.some(
    (account) => account.externalAccountId && !isOpenBankingAccountDeleted(account),
  )

  const { open: openFrodaLendingEligibilityModal } = useModal(ModalId.FrodaLendingEligibilityModal)
  const { open: openFrodaProcessingModal, close: closeFrodaProcessingModal } = useModal(ModalId.FrodaProcessingModal)

  const { error_code: errorCode, token: redirectToken, auth_code: authCode } = queryParams

  // bugfix for KRP-2566, params are sometimes not read properly on initial load
  const currentSearchParams = new URLSearchParams(window.location.search)
  const hasRedirectParams = Boolean(
    currentSearchParams.get('token') || currentSearchParams.get('error_code') || currentSearchParams.get('auth_code'),
  )
  const currentUrl = window.location.href.split('?')[0]
  const parentUrl = encodeURIComponent(currentUrl)

  const eligibilityRequirements: FrodaLendingEligibilityRequirements = {
    hasConnectedBank,
    hasRegistrationNo: !!organization?.registrationNo,
    isUserDataComplete: Boolean((user?.name?.trim()?.split(' ')?.length ?? 0) > 1 && user?.email && user?.phone),
  }

  const isEligibleForLending = Object.values(eligibilityRequirements).every(Boolean)

  const { homeAccessUrl, isAccessUrlLoading, requestFrodaHomeAccessUrl, setHomeAccessUrl } = useFrodaHomeAccessUrl()

  const { onboardUser } = useFrodaOnboarding(openFrodaProcessingModal, closeFrodaProcessingModal, setHomeAccessUrl)

  const { onGetOfferClick } = useGetOfferClick({
    isEligibleForLending,
    organization,
    openFrodaLendingEligibilityModal,
    setIsProcessing,
    onboardUser,
  })

  useGenerateFrodaRedirectUrl(setHomeAccessUrl, parentUrl, redirectToken, errorCode, authCode)

  useEffect(() => {
    if (!hasRedirectParams && homeAccessUrl === undefined && organization?.id) {
      requestFrodaHomeAccessUrl()
    }
  }, [hasRedirectParams, organization?.id, requestFrodaHomeAccessUrl, homeAccessUrl])

  useEffect(() => {
    const initialIsBookkeepingHeroHidden = initialIsBookkeepingHeroHiddenRef.current
    if (homeAccessUrl) {
      toggleBookkeepingHero(true)
    }

    return () => {
      toggleBookkeepingHero(initialIsBookkeepingHeroHidden)
    }
  }, [homeAccessUrl, toggleBookkeepingHero])

  return (
    <FrodaFinanceLendingContext.Provider
      value={{
        eligibilityRequirements,
        homeAccessUrl,
        isAccessUrlLoading,
        isProcessing,
        onboardUser,
        onGetOfferClick,
        parentUrl,
        setIsProcessing,
        setTermsAccepted,
        termsAccepted,
      }}
    >
      {children}
    </FrodaFinanceLendingContext.Provider>
  )
}

export const useFrodaFinanceLendingContext = () => {
  const context = useContext(FrodaFinanceLendingContext)
  if (!context) {
    throw new Error('useFrodaFinanceLendingContext must be used within FrodaFinanceLendingProviderContext')
  }
  return context
}
